// src/components/GeneratePayment.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
} from "@mui/material";

/**
 * Helper: Convert the Trello card id to a creation date string in YYYYMMDD format.
 */
const getCreationDate = (cardId) => {
  const timestamp = parseInt(cardId.substring(0, 8), 16) * 1000;
  const date = new Date(timestamp);
  const yyyy = date.getFullYear();
  const mm = ("0" + (date.getMonth() + 1)).slice(-2);
  const dd = ("0" + date.getDate()).slice(-2);
  return `${yyyy}${mm}${dd}`;
};

/**
 * Extract the 2PAY comment from a card’s comment actions.
 */
const extract2PAYComment = (actions) => {
  if (!actions) return "";
  if (!Array.isArray(actions)) {
    actions = [actions];
  }
  const commentAction = actions.find(
    (act) =>
      act.data &&
      act.data.text &&
      act.data.text.trim().toUpperCase().startsWith("2PAY")
  );
  if (!commentAction) return "";
  return commentAction.data.text.trim();
};

/**
 * Parse the payment comment.
 */
const parsePaymentComment = (actions) => {
  const comment = extract2PAYComment(actions);
  console.log("Extracted 2PAY comment:", comment);
  if (!comment) {
    console.log("No 2PAY comment found.");
    return { taskId: "Invalid", value: "Invalid", taskType: "Invalid" };
  }
  const parts = comment.split("-");
  console.log("Split parts:", parts);
  if (parts.length !== 3 || parts[0].toUpperCase() !== "2PAY") {
    console.log("Invalid format: Incorrect parts or prefix. Received:", parts[0]);
    return { taskId: "Invalid", value: "Invalid", taskType: "Invalid" };
  }
  const taskId = parts[1].trim();
  let valueStr = parts[2].trim().replace(/\s/g, "");
  console.log("Extracted taskId:", taskId);
  console.log("Extracted value string (whitespace removed):", valueStr);
  
  if (valueStr.endsWith("I")) {
    const qty = parseFloat(valueStr.slice(0, -1));
    console.log("Parsed quantity:", qty);
    if (isNaN(qty)) {
      console.log("Invalid quantity: not a number");
      return { taskId: "Invalid", value: "Invalid", taskType: "Invalid" };
    }
    return { taskId, value: qty, taskType: "Quantity Based" };
  }
  
  const timeRegex = /^((\d+)H)?((\d+)M)?$/;
  const match = timeRegex.exec(valueStr);
  console.log("Time regex match:", match);
  if (!match || (!match[2] && !match[4])) {
    console.log("Time regex did not match expected format");
    return { taskId: "Invalid", value: "Invalid", taskType: "Invalid" };
  }
  const hours = match[2] ? parseFloat(match[2]) : 0;
  const minutes = match[4] ? parseFloat(match[4]) : 0;
  console.log("Parsed hours:", hours, "Parsed minutes:", minutes);
  if (isNaN(hours) || isNaN(minutes)) {
    console.log("Invalid time: hours or minutes is not a number");
    return { taskId: "Invalid", value: "Invalid", taskType: "Invalid" };
  }
  const totalHours = Number((hours + minutes / 60).toFixed(2));
  console.log("Total hours computed (rounded):", totalHours);
  return { taskId, value: totalHours, taskType: "Time Based" };
};

/**
 * Download Excel report for approved cards.
 */
const downloadExcel = (cards, filename) => {
  const generatedDate = new Date();
  const formattedDate =
    generatedDate.getFullYear() +
    ("0" + (generatedDate.getMonth() + 1)).slice(-2) +
    ("0" + generatedDate.getDate()).slice(-2);

  const headerInfo = [
    ["AppJingle PMS"],
    ["Generated by AppJingle PMS"],
    [`Generated Date: ${formattedDate}`],
    []
  ];
  const tableHeader = [["Card Title", "Member", "Time/Quantity", "Task ID", "Price", "Amount", "Creation Date"]];
  const tableData = cards.map((card) => {
    const price = Math.round(parseFloat(card.price) || 0);
    const value = card.value !== undefined ? card.value : 0;
    const amount = price * value;
    return [card.title, card.member, value, card.taskId, price, amount, card.createdAt];
  });
  const totalAmount = tableData.reduce((acc, row) => acc + (row[5] || 0), 0);
  const totalRow = [["", "", "", "", "Total", totalAmount, ""]];
  const data = [...headerInfo, ...tableHeader, ...tableData, ...totalRow];
  const ws = XLSX.utils.aoa_to_sheet(data);
  const headerRowIndex = headerInfo.length;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let C = range.s.c; C <= range.e.c; C++) {
    const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: C });
    if (!ws[cellAddress]) continue;
    ws[cellAddress].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: "FFFF00" } }
    };
  }
  ws["!cols"] = [
    { wch: 30 },
    { wch: 20 },
    { wch: 15 },
    { wch: 15 },
    { wch: 10 },
    { wch: 15 },
    { wch: 15 }
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Approved Report");
  XLSX.writeFile(wb, filename);
};

/**
 * PDF Download Helper (using jsPDF)
 */
const downloadPDF = (cards, reportTitle, filename) => {
  const doc = new jsPDF();
  doc.setFontSize(16);
  doc.text(reportTitle, 14, 20);
  doc.setFontSize(10);
  const headers = ["Card Title", "Member", "Time/Quantity", "Task ID", "Price", "Amount", "Creation Date"];
  let startY = 30;
  doc.text(headers.join(" | "), 14, startY);
  startY += 10;
  cards.forEach((card) => {
    const price = Math.round(parseFloat(card.price) || 0);
    const value = card.value !== undefined ? card.value : 0;
    const amount = (price * value).toString();
    const row = [
      card.title,
      card.member,
      String(value),
      String(card.taskId || "N/A"),
      String(price),
      amount,
      String(card.createdAt)
    ].join(" | ");
    doc.text(row, 14, startY);
    startY += 10;
    if (startY > 280) {
      doc.addPage();
      startY = 20;
    }
  });
  doc.save(filename);
};

/**
 * Excel Download Helper for Rejected Cards.
 */
const downloadExcelRejected = (cards, filename) => {
  const generatedDate = new Date();
  const formattedDate =
    generatedDate.getFullYear() +
    ("0" + (generatedDate.getMonth() + 1)).slice(-2) +
    ("0" + generatedDate.getDate()).slice(-2);

  const headerInfo = [
    ["AppJingle PMS"],
    ["Generated by AppJingle PMS"],
    [`Generated Date: ${formattedDate}`],
    []
  ];
  const tableHeader = [["Card Title", "Member", "Rejection Comment", "Creation Date"]];
  const tableData = cards.map((card) => [card.title, card.member, card.rejectionComment || "", card.createdAt]);
  const data = [...headerInfo, ...tableHeader, ...tableData];
  const ws = XLSX.utils.aoa_to_sheet(data);
  const headerRowIndex = headerInfo.length;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let C = range.s.c; C <= range.e.c; C++) {
    const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: C });
    if (!ws[cellAddress]) continue;
    ws[cellAddress].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: "FFFF00" } }
    };
  }
  ws["!cols"] = [
    { wch: 30 },
    { wch: 20 },
    { wch: 40 },
    { wch: 15 }
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Rejected Report");
  XLSX.writeFile(wb, filename);
};

const GeneratePayment = () => {
  // Define Trello API credentials.
  const trelloKey = "18d50606d419c001df43c639bef31408";
  const trelloToken = "f847d5371155e21120eb8c5f1ef5b57a22fa331789125143db766cbd147290b3";

  // State for cards.
  const [pendingCards, setPendingCards] = useState([]);
  const [approvedCards, setApprovedCards] = useState([]);
  const [rejectedCards, setRejectedCards] = useState([]);

  // Loader and error state.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // Bulk selections and bulk price.
  const [selectedApproved, setSelectedApproved] = useState([]);
  const [selectedPending, setSelectedPending] = useState([]);
  const [bulkPrice, setBulkPrice] = useState("");

  // Bulk action loader and progress states.
  const [bulkApproveLoading, setBulkApproveLoading] = useState(false);
  const [bulkRejectLoading, setBulkRejectLoading] = useState(false);
  const [bulkMarkAsPaidLoading, setBulkMarkAsPaidLoading] = useState(false);
  const [bulkMarkAsPaidProgress, setBulkMarkAsPaidProgress] = useState(0);
  const [bulkMarkAsPaidTotal, setBulkMarkAsPaidTotal] = useState(0);

  // New state to track if an error occurred during bulk mark as paid.
  const [bulkMarkAsPaidErrorOccurred, setBulkMarkAsPaidErrorOccurred] = useState(false);
  // State to accumulate the log.
  const [bulkLog, setBulkLog] = useState("");

  // Snackbar states.
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Dropdown filters.
  const [filterPendingMember, setFilterPendingMember] = useState("");
  const [filterPendingTaskType, setFilterPendingTaskType] = useState("");
  const [filterPendingTaskId, setFilterPendingTaskId] = useState("");
  const [filterApprovedMember, setFilterApprovedMember] = useState("");
  const [filterApprovedTaskId, setFilterApprovedTaskId] = useState("");
  const [filterRejectedMember, setFilterRejectedMember] = useState("");

  // Pagination state.
  const [pendingPage, setPendingPage] = useState(0);
  const [pendingRowsPerPage, setPendingRowsPerPage] = useState(5);
  const [approvedPage, setApprovedPage] = useState(0);
  const [approvedRowsPerPage, setApprovedRowsPerPage] = useState(5);
  const [rejectedPage, setRejectedPage] = useState(0);
  const [rejectedRowsPerPage, setRejectedRowsPerPage] = useState(5);

  // Member mapping (setter only).
  const [, setMemberMapping] = useState({});

  // State for PAID list ID.
  const [paidListId, setPaidListId] = useState("");

  // --------------------------------------------------------------------
  // Fetch cards and lists from Trello.
  // --------------------------------------------------------------------
  useEffect(() => {
    const fetchCardsFromBoardList = async () => {
      const targetListName = "Sign Payment - To Pay";
      const boardName = "AppJingle Editors";
      console.log("Using Board:", boardName, "and List:", targetListName);
      try {
        const boardsResponse = await axios.get(
          `https://api.trello.com/1/members/me/boards?fields=id,name&key=${trelloKey}&token=${trelloToken}`
        );
        const boards = boardsResponse.data;
        console.log("Boards fetched:", boards);
        const targetBoard = boards.find(
          (b) => b.name.toLowerCase() === boardName.toLowerCase()
        );
        if (!targetBoard) {
          setError(`Board "${boardName}" not found.`);
          setIsLoading(false);
          return;
        }
        console.log("Found board:", targetBoard);
        const membersResponse = await axios.get(
          `https://api.trello.com/1/boards/${targetBoard.id}/members?fields=fullName&key=${trelloKey}&token=${trelloToken}`
        );
        const members = membersResponse.data;
        const mapping = {};
        members.forEach((m) => {
          mapping[m.id] = m.fullName;
        });
        console.log("Member mapping:", mapping);
        setMemberMapping(mapping);
        const listsResponse = await axios.get(
          `https://api.trello.com/1/boards/${targetBoard.id}/lists?fields=id,name&key=${trelloKey}&token=${trelloToken}`
        );
        const lists = listsResponse.data;
        console.log("Lists fetched:", lists);
        const targetList = lists.find(
          (list) => list.name.toLowerCase() === targetListName.toLowerCase()
        );
        if (!targetList) {
          setError(`List "${targetListName}" not found in board "${boardName}".`);
          setIsLoading(false);
          return;
        }
        console.log("Found target list:", targetList);
        const paidList = lists.find(
          (list) => list.name.toLowerCase() === "paid"
        );
        if (!paidList) {
          console.warn("PAID list not found on board. 'Mark as paid' action will not work properly.");
        }
        setPaidListId(paidList ? paidList.id : "");
        const cardsResponse = await axios.get(
          `https://api.trello.com/1/lists/${targetList.id}/cards?fields=name,shortUrl,dateLastActivity,idMembers&actions=commentCard&actions_limit=100&key=${trelloKey}&token=${trelloToken}`
        );
        const cards = cardsResponse.data.map((card) => ({
          id: card.id,
          title: card.name,
          member:
            card.idMembers && card.idMembers.length > 0
              ? mapping[card.idMembers[0]] || card.idMembers[0]
              : "Unknown",
          trelloLink: card.shortUrl,
          comment: card.actions && Array.isArray(card.actions) ? card.actions : [],
          createdAt: getCreationDate(card.id),
        }));
        console.log("Cards after filtering by board and list:", cards.length);
        setPendingCards(cards);
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching cards:", err);
        setError("Failed to fetch cards. Please check your API key/token and permissions.");
        setIsLoading(false);
      }
    };

    fetchCardsFromBoardList();
  }, [trelloKey, trelloToken]);

  // --------------------------------------------------------------------
  // Local filtering.
  // --------------------------------------------------------------------
  const filteredPendingCards = pendingCards.filter((card) => {
    const parsed = parsePaymentComment(card.comment);
    return (
      (filterPendingMember ? card.member === filterPendingMember : true) &&
      (filterPendingTaskType ? parsed.taskType === filterPendingTaskType : true) &&
      (filterPendingTaskId ? parsed.taskId === filterPendingTaskId : true)
    );
  });
  const filteredApprovedCards = approvedCards.filter((card) => {
    return (
      (filterApprovedMember ? card.member === filterApprovedMember : true) &&
      (filterApprovedTaskId ? card.taskId === filterApprovedTaskId : true)
    );
  });
  const filteredRejectedCards = rejectedCards.filter((card) =>
    filterRejectedMember ? card.member === filterRejectedMember : true
  );

  // --------------------------------------------------------------------
  // Unique dropdown values.
  // --------------------------------------------------------------------
  const pendingMembers = [...new Set(pendingCards.map((card) => card.member))];
  const pendingTaskTypes = [
    ...new Set(
      pendingCards.map((card) => {
        const parsed = parsePaymentComment(card.comment);
        return parsed.taskType;
      })
    ),
  ];
  const pendingTaskIds = [
    ...new Set(pendingCards.map((card) => parsePaymentComment(card.comment).taskId)),
  ];
  const approvedMembers = [...new Set(approvedCards.map((card) => card.member))];
  const approvedTaskIds = [...new Set(approvedCards.map((card) => card.taskId))];
  const rejectedMembers = [...new Set(rejectedCards.map((card) => card.member))];

  // --------------------------------------------------------------------
  // Pagination.
  // --------------------------------------------------------------------
  const paginatedPendingCards = filteredPendingCards.slice(
    pendingPage * pendingRowsPerPage,
    pendingPage * pendingRowsPerPage + pendingRowsPerPage
  );
  const paginatedApprovedCards = filteredApprovedCards.slice(
    approvedPage * approvedRowsPerPage,
    approvedPage * approvedRowsPerPage + approvedRowsPerPage
  );
  const paginatedRejectedCards = filteredRejectedCards.slice(
    rejectedPage * rejectedRowsPerPage,
    rejectedPage * rejectedRowsPerPage + rejectedRowsPerPage
  );

  // --------------------------------------------------------------------
  // Handlers for Pending Cards: Single Approve/Reject.
  // --------------------------------------------------------------------
  const handleApprove = (card) => {
    const parsed = parsePaymentComment(card.comment);
    const approvedCard = { ...card, ...parsed, price: 0 };
    setApprovedCards((prev) => [...prev, approvedCard]);
    setPendingCards((prev) => prev.filter((c) => c.id !== card.id));
    setSelectedPending((prev) => prev.filter((id) => id !== card.id));
  };

  const handleReject = (card) => {
    const parsed = parsePaymentComment(card.comment);
    const rejectionComment =
      "Card has missing payment comment or comment has wrong task id/value etc. Please contact your HR manager for more detail";
    const rejectedCard = { ...card, ...parsed, rejectionComment };
    setRejectedCards((prev) => [...prev, rejectedCard]);
    setPendingCards((prev) => prev.filter((c) => c.id !== card.id));
    setSelectedPending((prev) => prev.filter((id) => id !== card.id));
  };

  // --------------------------------------------------------------------
  // Bulk Action Handlers for Pending Cards.
  // --------------------------------------------------------------------
  const handleBulkApprove = async () => {
    setBulkApproveLoading(true);
    try {
      const cardsToApprove = pendingCards.filter((card) =>
        selectedPending.includes(card.id)
      );
      const newApprovedCards = cardsToApprove.map((card) => ({
        ...card,
        ...parsePaymentComment(card.comment),
        price: 0,
      }));
      setApprovedCards((prev) => [...prev, ...newApprovedCards]);
      setPendingCards((prev) =>
        prev.filter((card) => !selectedPending.includes(card.id))
      );
      setSelectedPending([]);
    } catch (error) {
      console.error(error);
      showSnackbar("Error in Bulk Approve: " + error.message, "error");
    } finally {
      setBulkApproveLoading(false);
    }
  };

  const handleBulkReject = async () => {
    setBulkRejectLoading(true);
    try {
      const cardsToReject = pendingCards.filter((card) =>
        selectedPending.includes(card.id)
      );
      const newRejectedCards = cardsToReject.map((card) => {
        const parsed = parsePaymentComment(card.comment);
        return {
          ...card,
          ...parsed,
          rejectionComment:
            "Card has missing payment comment or comment has wrong task id/value etc. Please contact your HR manager for more detail",
        };
      });
      setRejectedCards((prev) => [...prev, ...newRejectedCards]);
      setPendingCards((prev) =>
        prev.filter((card) => !selectedPending.includes(card.id))
      );
      setSelectedPending([]);
    } catch (error) {
      console.error(error);
      showSnackbar("Error in Bulk Reject: " + error.message, "error");
    } finally {
      setBulkRejectLoading(false);
    }
  };

  // --------------------------------------------------------------------
  // Improved Bulk Action: Mark as Paid (sequentially with delay, stop on error).
  // --------------------------------------------------------------------
  const handleBulkMarkAsPaid = async () => {
    if (!paidListId) {
      showSnackbar("PAID list ID is not available.", "error");
      return;
    }
    const cardsToMark = pendingCards.filter((card) =>
      selectedPending.includes(card.id)
    );
    if (cardsToMark.length === 0) return;
    setBulkMarkAsPaidTotal(cardsToMark.length);
    setBulkMarkAsPaidProgress(0);
    setBulkMarkAsPaidLoading(true);
    setBulkMarkAsPaidErrorOccurred(false);
    setBulkLog(""); // Reset log

    // Process cards sequentially.
    try {
      for (const card of cardsToMark) {
        try {
          // Add the "PAID" comment.
          await axios.post(
            `https://api.trello.com/1/cards/${card.id}/actions/comments?key=${trelloKey}&token=${trelloToken}`,
            { text: "PAID" }
          );
          // Move the card to the PAID list.
          await axios.put(
            `https://api.trello.com/1/cards/${card.id}?idList=${paidListId}&key=${trelloKey}&token=${trelloToken}`
          );
          // Update log and progress.
          setBulkLog((prev) => prev + `\nCard "${card.title}" processed successfully.`);
          setBulkMarkAsPaidProgress((prev) => prev + 1);
          // Delay between cards (e.g., 300ms) to respect API limits.
          await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (error) {
          setBulkLog((prev) => prev + `\nError processing card "${card.title}": ${error.message}`);
          setBulkMarkAsPaidErrorOccurred(true);
          showSnackbar(`Error processing card "${card.title}": ${error.message}`, "error");
          // Stop processing further cards.
          throw error;
        }
      }
      // If all cards processed successfully, remove them from pending.
      setPendingCards((prev) =>
        prev.filter((card) => !selectedPending.includes(card.id))
      );
      setSelectedPending([]);
    } catch (error) {
      // Processing stopped due to error.
      console.error("Bulk Mark as Paid halted:", error);
    } finally {
      setBulkMarkAsPaidLoading(false);
    }
  };

  // Function to download the bulk log.
  const downloadBulkLog = () => {
    const blob = new Blob([bulkLog], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bulk_mark_as_paid_log.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSelectPending = (cardId) => {
    setSelectedPending((prev) =>
      prev.includes(cardId) ? prev.filter((id) => id !== cardId) : [...prev, cardId]
    );
  };

  const allSelectedPending =
    filteredPendingCards.length > 0 &&
    filteredPendingCards.every((card) => selectedPending.includes(card.id));
  const someSelectedPending =
    filteredPendingCards.some((card) => selectedPending.includes(card.id)) &&
    !allSelectedPending;
  const handleHeaderCheckboxChangePending = (e) => {
    if (e.target.checked) {
      setSelectedPending(filteredPendingCards.map((card) => card.id));
    } else {
      setSelectedPending([]);
    }
  };

  // --------------------------------------------------------------------
  // Handlers for Approved Cards.
  // --------------------------------------------------------------------
  const handleSelectApproved = (cardId) => {
    setSelectedApproved((prev) =>
      prev.includes(cardId) ? prev.filter((id) => id !== cardId) : [...prev, cardId]
    );
  };

  const allSelectedApproved =
    filteredApprovedCards.length > 0 &&
    filteredApprovedCards.every((card) => selectedApproved.includes(card.id));
  const someSelectedApproved =
    filteredApprovedCards.some((card) => selectedApproved.includes(card.id)) &&
    !allSelectedApproved;
  const handleHeaderCheckboxChangeApproved = (e) => {
    if (e.target.checked) {
      setSelectedApproved(filteredApprovedCards.map((card) => card.id));
    } else {
      setSelectedApproved([]);
    }
  };

  // Round off individual price updates.
  const handlePriceChange = (cardId, newPrice) => {
    const roundedPrice = Math.round(parseFloat(newPrice) || 0);
    setApprovedCards((prev) =>
      prev.map((card) => (card.id === cardId ? { ...card, price: roundedPrice } : card))
    );
  };

  // Bulk price update.
  const handleBulkPriceUpdate = () => {
    const roundedBulkPrice = Math.round(parseFloat(bulkPrice) || 0);
    setApprovedCards((prev) =>
      prev.map((card) =>
        selectedApproved.includes(card.id)
          ? { ...card, price: roundedBulkPrice }
          : card
      )
    );
    setBulkPrice("");
    setSelectedApproved([]);
  };

  const totalAmount = filteredApprovedCards.reduce((total, card) => {
    const amount = (parseFloat(card.price) || 0) * (card.value || 0);
    return total + amount;
  }, 0);

  // --------------------------------------------------------------------
  // Download Handlers.
  // --------------------------------------------------------------------
  const handleDownloadPDF = (member) => {
    const memberCards = filteredApprovedCards.filter((card) => card.member === member);
    downloadPDF(memberCards, `Payment Report for ${member}`, `Payment_Report_${member}.pdf`);
  };

  const handleDownloadExcel = (member) => {
    const memberCards = filteredApprovedCards.filter((card) => card.member === member);
    downloadExcel(memberCards, `Payment_Report_${member}.xlsx`);
  };

  const handleDownloadRejectedPDF = () => {
    downloadPDF(filteredRejectedCards, "Rejected Cards Report", "Rejected_Report.pdf");
  };

  const handleDownloadRejectedExcel = () => {
    downloadExcelRejected(filteredRejectedCards, "Rejected_Report.xlsx");
  };

  // --------------------------------------------------------------------
  // Auto-adjust pagination.
  // --------------------------------------------------------------------
  useEffect(() => {
    if (pendingPage > 0 && pendingPage * pendingRowsPerPage >= filteredPendingCards.length) {
      setPendingPage(0);
    }
  }, [filteredPendingCards, pendingPage, pendingRowsPerPage]);

  useEffect(() => {
    if (approvedPage > 0 && approvedPage * approvedRowsPerPage >= filteredApprovedCards.length) {
      setApprovedPage(0);
    }
  }, [filteredApprovedCards, approvedPage, approvedRowsPerPage]);

  useEffect(() => {
    if (rejectedPage > 0 && rejectedPage * rejectedRowsPerPage >= filteredRejectedCards.length) {
      setRejectedPage(0);
    }
  }, [filteredRejectedCards, rejectedPage, rejectedRowsPerPage]);

  // --------------------------------------------------------------------
  // Render Loader, Error Message, or the Content.
  // --------------------------------------------------------------------
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }
  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Generate Payment
        </Typography>
        <Typography variant="body2" gutterBottom>
          This feature allows you to generate and finalize payment reports.
        </Typography>

        {/* ===================== */}
        {/* Pending Cards Table */}
        {/* ===================== */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Pending Payment Cards (Sign Payment – To Pay)
          </Typography>
          <Typography variant="caption" display="block" gutterBottom>
            Total: {pendingCards.length} | Filtered: {filteredPendingCards.length}
          </Typography>
          {selectedPending.length > 0 && (
            <Typography variant="subtitle2">
              Total Selected: {selectedPending.length}
            </Typography>
          )}
          <Box display="flex" gap={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Member</InputLabel>
              <Select
                value={filterPendingMember}
                label="Member"
                onChange={(e) => {
                  setFilterPendingMember(e.target.value);
                  setPendingPage(0);
                }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {pendingMembers.map((member) => (
                  <MenuItem key={member} value={member}>
                    {member}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Task Type</InputLabel>
              <Select
                value={filterPendingTaskType}
                label="Task Type"
                onChange={(e) => {
                  setFilterPendingTaskType(e.target.value);
                  setPendingPage(0);
                }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {pendingTaskTypes.map((tt) => (
                  <MenuItem key={tt} value={tt}>
                    {tt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Task ID</InputLabel>
              <Select
                value={filterPendingTaskId}
                label="Task ID"
                onChange={(e) => {
                  setFilterPendingTaskId(e.target.value);
                  setPendingPage(0);
                }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {pendingTaskIds.map((tid) => (
                  <MenuItem key={tid} value={tid}>
                    {tid}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {selectedPending.length > 0 && (
            <Box mb={2} display="flex" gap={2}>
              <Button
                variant="contained"
                color="success"
                onClick={handleBulkApprove}
                disabled={bulkApproveLoading}
              >
                {bulkApproveLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Bulk Approve (${selectedPending.length})`
                )}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleBulkReject}
                disabled={bulkRejectLoading}
              >
                {bulkRejectLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Bulk Reject (${selectedPending.length})`
                )}
              </Button>
              <Button
                variant="contained"
                color="info"
                onClick={handleBulkMarkAsPaid}
                disabled={bulkMarkAsPaidLoading}
              >
                {bulkMarkAsPaidLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Bulk Mark as Paid (${selectedPending.length})`
                )}
              </Button>
            </Box>
          )}
          {bulkMarkAsPaidLoading && (
            <Box mt={1}>
              <Typography variant="caption">
                Mark as Paid Progress: {bulkMarkAsPaidProgress} / {bulkMarkAsPaidTotal}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  bulkMarkAsPaidTotal
                    ? (bulkMarkAsPaidProgress / bulkMarkAsPaidTotal) * 100
                    : 0
                }
              />
            </Box>
          )}
          {bulkMarkAsPaidErrorOccurred && !bulkMarkAsPaidLoading && (
            <Box mt={1}>
              <Button variant="outlined" onClick={downloadBulkLog}>
                Download Error Log
              </Button>
            </Box>
          )}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someSelectedPending}
                      checked={allSelectedPending}
                      onChange={handleHeaderCheckboxChangePending}
                    />
                  </TableCell>
                  <TableCell>Card Title</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Trello Card Link</TableCell>
                  <TableCell>Task Type</TableCell>
                  <TableCell>Task ID</TableCell>
                  <TableCell>Time/Quantity</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPendingCards.map((card) => {
                  const parsed = parsePaymentComment(card.comment);
                  return (
                    <TableRow key={card.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedPending.includes(card.id)}
                          onChange={() => handleSelectPending(card.id)}
                        />
                      </TableCell>
                      <TableCell>{card.title}</TableCell>
                      <TableCell>{card.member}</TableCell>
                      <TableCell>
                        <a href={card.trelloLink} target="_blank" rel="noopener noreferrer">
                          Link
                        </a>
                      </TableCell>
                      <TableCell>{parsed.taskType}</TableCell>
                      <TableCell>{parsed.taskId}</TableCell>
                      <TableCell>{parsed.value}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleApprove(card)}
                          sx={{ mr: 1 }}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleReject(card)}
                        >
                          Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {paginatedPendingCards.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No pending cards.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredPendingCards.length}
            page={pendingPage}
            onPageChange={(event, newPage) => setPendingPage(newPage)}
            rowsPerPage={pendingRowsPerPage}
            onRowsPerPageChange={(event) => {
              setPendingRowsPerPage(parseInt(event.target.value, 10));
              setPendingPage(0);
            }}
          />
        </Box>
  
        {/* ===================== */}
        {/* Approved Cards Table */}
        {/* ===================== */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Approved Payment Cards
          </Typography>
          <Typography variant="caption" display="block" gutterBottom>
            Total: {approvedCards.length} | Filtered: {filteredApprovedCards.length}
          </Typography>
          <Box display="flex" gap={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Member</InputLabel>
              <Select
                value={filterApprovedMember}
                label="Member"
                onChange={(e) => {
                  setFilterApprovedMember(e.target.value);
                  setApprovedPage(0);
                }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {approvedMembers.map((member) => (
                  <MenuItem key={member} value={member}>
                    {member}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Task ID</InputLabel>
              <Select
                value={filterApprovedTaskId}
                label="Task ID"
                onChange={(e) => {
                  setFilterApprovedTaskId(e.target.value);
                  setApprovedPage(0);
                }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {approvedTaskIds.map((tid) => (
                  <MenuItem key={tid} value={tid}>
                    {tid}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someSelectedApproved}
                      checked={allSelectedApproved}
                      onChange={handleHeaderCheckboxChangeApproved}
                    />
                  </TableCell>
                  <TableCell>Creation Date</TableCell>
                  <TableCell>Card Title</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Time/Quantity</TableCell>
                  <TableCell>Task ID</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedApprovedCards.map((card) => (
                  <TableRow key={card.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedApproved.includes(card.id)}
                        onChange={() => handleSelectApproved(card.id)}
                      />
                    </TableCell>
                    <TableCell>{card.createdAt}</TableCell>
                    <TableCell>
                      <a href={card.trelloLink} target="_blank" rel="noopener noreferrer">
                        {card.title}
                      </a>
                    </TableCell>
                    <TableCell>{card.member}</TableCell>
                    <TableCell>{card.value}</TableCell>
                    <TableCell>{card.taskId}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={card.price}
                        onChange={(e) => handlePriceChange(card.id, e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      {(parseFloat(card.price) || 0) * (card.value || 0)}
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedApprovedCards.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No approved cards.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {selectedApproved.length > 0 && (
            <Box mt={2} display="flex" alignItems="center" gap={2}>
              <TextField
                label="Bulk Price"
                type="number"
                size="small"
                value={bulkPrice}
                onChange={(e) => setBulkPrice(e.target.value)}
              />
              <Button variant="contained" onClick={handleBulkPriceUpdate}>
                Apply Bulk Price
              </Button>
            </Box>
          )}
          <Box mt={2}>
            <Typography variant="subtitle1">Total Amount: {totalAmount}</Typography>
          </Box>
          <Box mt={2}>
            <Typography variant="subtitle1">Download Generated Payment Reports:</Typography>
            {approvedMembers.map((member) => (
              <Box key={member} mt={1} display="flex" alignItems="center" gap={2}>
                <Typography>{member}</Typography>
                <Button variant="outlined" onClick={() => handleDownloadPDF(member)}>
                  Download PDF
                </Button>
                <Button variant="outlined" onClick={() => handleDownloadExcel(member)}>
                  Download Excel
                </Button>
              </Box>
            ))}
          </Box>
          <TablePagination
            component="div"
            count={filteredApprovedCards.length}
            page={approvedPage}
            onPageChange={(event, newPage) => setApprovedPage(newPage)}
            rowsPerPage={approvedRowsPerPage}
            onRowsPerPageChange={(event) => {
              setApprovedRowsPerPage(parseInt(event.target.value, 10));
              setApprovedPage(0);
            }}
          />
        </Box>
  
        {/* ===================== */}
        {/* Rejected Cards Table */}
        {/* ===================== */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Rejected Payment Cards
          </Typography>
          <Typography variant="caption" display="block" gutterBottom>
            Total: {rejectedCards.length} | Filtered: {filteredRejectedCards.length}
          </Typography>
          <Box display="flex" gap={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Member</InputLabel>
              <Select
                value={filterRejectedMember}
                label="Member"
                onChange={(e) => {
                  setFilterRejectedMember(e.target.value);
                  setRejectedPage(0);
                }}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {rejectedMembers.map((member) => (
                  <MenuItem key={member} value={member}>
                    {member}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Card Title</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Trello Card Link</TableCell>
                  <TableCell>Rejection Comment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRejectedCards.map((card) => (
                  <TableRow key={card.id}>
                    <TableCell>{card.title}</TableCell>
                    <TableCell>{card.member}</TableCell>
                    <TableCell>
                      <a href={card.trelloLink} target="_blank" rel="noopener noreferrer">
                        Link
                      </a>
                    </TableCell>
                    <TableCell>{card.rejectionComment}</TableCell>
                  </TableRow>
                ))}
                {paginatedRejectedCards.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No rejected cards.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2} display="flex" gap={2}>
            <Button variant="outlined" onClick={handleDownloadRejectedPDF}>
              Download Rejected Report (PDF)
            </Button>
            <Button variant="outlined" onClick={handleDownloadRejectedExcel}>
              Download Rejected Report (Excel)
            </Button>
          </Box>
          <TablePagination
            component="div"
            count={filteredRejectedCards.length}
            page={rejectedPage}
            onPageChange={(event, newPage) => setRejectedPage(newPage)}
            rowsPerPage={rejectedRowsPerPage}
            onRowsPerPageChange={(event) => {
              setRejectedRowsPerPage(parseInt(event.target.value, 10));
              setRejectedPage(0);
            }}
          />
        </Box>
  
        {/* Snackbar for error notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default GeneratePayment;
